import { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import { analyticsService } from '../../../api/analytics.service';
import { fDateTime } from '../../../utils/formatTime';
import { formatLabel } from '../../../utils/formatLabel';

type Props = {
  patientId: string;
};

export default function PatientCommunicationLog({ patientId }: Props) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response =
          await analyticsService.getPatientCommunicationHistory(patientId);
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching communication history:', error);
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchHistory();
    }
  }, [patientId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Communication History
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Sent By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((log) => (
              <TableRow key={log.id}>
                <TableCell>{fDateTime(log.createdDate)}</TableCell>
                <TableCell>
                  <Chip
                    label={log.channel}
                    size="small"
                    color={log.channel === 'SMS' ? 'primary' : 'secondary'}
                  />
                </TableCell>
                <TableCell>{log.type}</TableCell>
                <TableCell>
                  <Chip
                    label={formatLabel(log.status)}
                    size="small"
                    color={
                      log.status === 'Sent'
                        ? 'success'
                        : log.status === 'Failed'
                          ? 'error'
                          : 'warning'
                    }
                  />
                </TableCell>
                <TableCell
                  title={log.content}
                  sx={{
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {log.content}
                </TableCell>
                <TableCell>
                  {log.staff
                    ? `${log.staff.firstName} ${log.staff.lastName}`
                    : 'System'}
                </TableCell>
              </TableRow>
            ))}
            {history.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No communication history found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
